import type React from "react";
import { useEffect, useState } from "react";

import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { invariant } from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { StyledInput } from "shared/atoms/inputs/StyledInputs";
import { MUISwitch } from "shared/atoms/MUI/MUISwitch";
import { Notification } from "shared/atoms/Notification";

import { ButtonWrapper, EditFieldContainer, HeaderRow, fieldDisplay, fieldWithBorder } from "../shared/styles";

export const WeeklyPatientLimit: React.FC = () => {
  const { profile, setProfile } = useProfileContext();
  const [isEditing, setIsEditing] = useState(false);
  const putTherapistMutation = usePutTherapistMutation();
  const { handleSubmit, register, reset, watch, setValue } = useForm({
    defaultValues: {
      weekly_patient_assignment_limit: profile?.therapist_profile.weekly_assignment_limit || null,
      weekly_patient_assignment_limit_enabled: !!profile?.therapist_profile.weekly_assignment_limit || false,
    },
  });

  const watchWeeklyLimitEnabled: boolean = watch("weekly_patient_assignment_limit_enabled", false);

  useEffect(() => {
    if (!watchWeeklyLimitEnabled && isEditing) {
      setValue("weekly_patient_assignment_limit", null);
    }
  }, [watchWeeklyLimitEnabled, isEditing, setValue]);

  const weeklyPatientLimit: number | null = profile?.therapist_profile?.weekly_assignment_limit ?? null;

  const { t } = useTranslation();

  const handleChangeWeeklyPatientLimitClick = () => {
    putTherapistMutation.reset();
    setIsEditing(!isEditing);
  };

  const handleCancelWeeklyPatientLimitClick = () => {
    putTherapistMutation.reset();
    setIsEditing(!isEditing);
    reset();
  };

  const onSubmit = async ({ weekly_patient_assignment_limit }: { weekly_patient_assignment_limit: number | null }) => {
    invariant(profile);

    const therapistId = profile.id;
    const data = {
      therapist_profile: {
        weekly_assignment_limit: weekly_patient_assignment_limit ?? null,
      },
    };
    const variables = { data, therapistId };

    putTherapistMutation.mutate(variables, {
      onSuccess(response) {
        setProfile(response);
        reset({
          weekly_patient_assignment_limit: data.therapist_profile.weekly_assignment_limit,
          weekly_patient_assignment_limit_enabled: data.therapist_profile.weekly_assignment_limit != null,
        });
        setIsEditing(!isEditing);
      },
    });
  };

  return (
    <WeeklyPatientLimitContainer data-testid="weekly-patient-limit">
      <form onSubmit={handleSubmit(onSubmit)}>
        {putTherapistMutation.isError && (
          <Notification variant="danger" style={{ margin: "5px 0" }}>
            {t("errors.generic")}
          </Notification>
        )}

        <HeaderRow>
          <Typography variant="subtitle1">{t("settings.weekly_patient_assignment_limit.title")}</Typography>
          {!isEditing && (
            <Box display="flex" justifyContent="center" alignItems="center" gap="8px">
              <WeeklyLimitValue data-testid="patient-limit-value">
                {weeklyPatientLimit ?? t("settings.weekly_patient_assignment_limit.unlimited")}
              </WeeklyLimitValue>
              <BaseButton
                variant="action"
                icon="pen"
                onClick={handleChangeWeeklyPatientLimitClick}
                data-testid="edit-patient-limit-button"
                size="small"
              />
            </Box>
          )}
        </HeaderRow>

        {isEditing && (
          <EditFieldContainer>
            <SwitchAndInputWrapper>
              <MUISwitch
                data-testid="patient-limit-switch"
                checked={watchWeeklyLimitEnabled}
                onChange={e => setValue("weekly_patient_assignment_limit_enabled", e.target.checked)}
              />
              <WeeklyLimitInput
                data-testid="patient-limit-input"
                {...register("weekly_patient_assignment_limit")}
                disabled={!watchWeeklyLimitEnabled}
                min="0"
                max="100"
                type="number"
                placeholder="0"
              />
            </SwitchAndInputWrapper>
          </EditFieldContainer>
        )}

        {isEditing && (
          <ButtonWrapper>
            <BaseButton
              type="button"
              onClick={handleCancelWeeklyPatientLimitClick}
              text={t("buttons.cancel")}
              variant="secondary"
              size="large"
            />
            <BaseButton type="submit" text={t("buttons.save")} size="large" />
          </ButtonWrapper>
        )}
      </form>
    </WeeklyPatientLimitContainer>
  );
};

const WeeklyPatientLimitContainer = styled.div`
  width: 100%;
  padding: 16px 0 7px 0;
  border-bottom: ${({ theme }) => theme.semantic.container.border};
`;

const WeeklyLimitValue = styled.div`
  ${fieldDisplay};
`;

const SwitchAndInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;

const WeeklyLimitInput = styled(StyledInput)`
  ${fieldWithBorder};
  max-width: 100px;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
